import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ANALYTICS_FALLBACK_USER_NAME } from '@shared';
import { EMPTY, from, switchMap, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import * as fromAuth from '../../auth/store/auth.reducer';
import * as fromLayout from '../../layout';
import { ldClient } from '../launch-darkly.client';
import { FeatureFlagsActions } from './feature-flags.actions';
import { FeatureFlagsState } from './feature-flags.reducer';

@Injectable()
export class FeatureFlagsEffects {
  private readonly actions$ = inject(Actions);
  private readonly authStore = inject(Store<fromAuth.AuthState>);
  private readonly layoutStore = inject(Store<fromLayout.LayoutState>);
  private readonly featureFlagsStore = inject(Store<FeatureFlagsState>);

  identifyUser$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FeatureFlagsActions.identifyUser),
        switchMap(({ user }) => {
          const isMobile = this.layoutStore.selectSignal(
            fromLayout.layoutFeature.selectIsMobile,
          )();
          const deviceType = isMobile ? 'mobile' : 'desktop';

          return ldClient
            ? from(
                /**
                 * Keys must be consistent, which means the same person must correspond
                 * to the same key across different services to contribute to consistent
                 * flag evaluations.
                 * https://docs.launchdarkly.com/sdk/features/context-config
                 */
                ldClient.identify({
                  ...environment.launchDarkly.context,
                  user: {
                    kind: 'user',
                    key: user.email,
                    email: user.email,
                    name: user.fullName ?? ANALYTICS_FALLBACK_USER_NAME,
                    sub: user.id,
                    anonymous: false,
                  },
                  device: {
                    key: deviceType,
                    name: deviceType,
                  },
                }),
              )
            : EMPTY;
        }),
      ),
    { dispatch: false },
  );

  onSetIsMobile$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromLayout.LayoutActions.setIsMobile),
        tap(() => {
          const user = this.authStore.selectSignal(
            fromAuth.authFeature.selectUser,
          )();
          if (user) {
            this.featureFlagsStore.dispatch(
              FeatureFlagsActions.identifyUser({ user }),
            );
          }
        }),
      ),
    { dispatch: false },
  );
}
